import React, { useState, useMemo } from "react";
import TableCompo from "../Table/TableCompo";
import { RiDeleteBin6Line } from "react-icons/ri";
import {
  Box,
  Typography,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  DialogContent,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  getSubscribedListRequest,
  transferBenefitsRequest,
} from "../../../redux/BullionDeposit/bullionDepositSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";

const SubscriberList = ({ refresh }) => {
  const dispatch = useDispatch();
  const { subscriberList, subscriberLoading, totalSubscribers } = useSelector(
    (state) => state.bullionDeposit,
  );
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [transferPercentage, setTransferPercentage] = useState("");
  const [searchText, setSearchText] = useState("");

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("permission parse error", e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "allSubscribers", "edit");

  const fetchData = () => {
    dispatch(
      getSubscribedListRequest({
        page: page + 1,
        limit: rowsPerPage,
        search: searchText.trim(),
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchText]);

  const handleTableChange = (action, payload) => {
    switch (action) {
      case "changePage":
        setPage(payload.page);
        break;
      case "changeRowsPerPage":
        setRowsPerPage(payload.rowsPerPage);
        setPage(0);
        break;
      case "search":
        setSearchText(payload.searchText);
        setPage(0);
        break;
      default:
        break;
    }
  };

  const paginatedData = (subscriberList || []).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const [statusFilter, setStatusFilter] = useState("");
  const filteredData = useMemo(() => {
    if (!statusFilter) return paginatedData;
    return paginatedData.filter((item) => {
      switch (statusFilter) {
        case "CUSTOMER STOPPED":
          return item.status === "CUSTOMERDISCONTINUED";
        case "JEWELLER STOPPED":
          return item.status === "JEWELLERDISCONTINUED";
        default:
          return item.status === statusFilter;
      }
    });
  }, [statusFilter, paginatedData]);

  const handleOpenModal = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedId(null);
  };

  const handleTransfer = () => {
    if (selectedId && transferPercentage !== "") {
      dispatch(
        transferBenefitsRequest({
          id: selectedId,
          transferpercentage: Number(transferPercentage),
        }),
      );
    }
    handleCloseModal();
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Creation Date",
      render: (val, row) => {
        if (!row.createdAt) return "N.A";

        const date = new Date(row.createdAt);
        const formatted = date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return <span title={formatted}>{formatted}</span>;
      },
    },
    {
      field: "customer",
      headerName: "Name",
      render: (val, row) => {
        const firstName = row?.customer?.firstName || "";
        const lastName = row?.customer?.lastName || "";
        return `${firstName} ${lastName}`.trim() || "N.A";
      },
    },
    {
      field: "bullion",
      headerName: "Metal",
      render: (val, row) => {
        const metalName = row?.bullion?.metalName || "N.A";
        const karat = row?.bullion?.karat ? `${row.bullion.karat}K` : "";
        const purity = row?.bullion?.purity ? `(${row.bullion.purity})` : "";

        const combined = [metalName, karat, purity].filter(Boolean).join(" ");
        return combined || "N.A";
      },
    },
    {
      field: "weight",
      headerName: "Weight (GMS)",
      render: (val, row) => {
        return row?.weight != null ? Number(row.weight).toFixed(2) : "N.A";
      },
    },
    {
      field: "depositplan",
      headerName: "Plan Name",
      render: (val, row) => {
        return row?.depositplan?.name ? row.depositplan.name : "N.A";
      },
    },
    {
      field: "PlanInterest",
      headerName: "Plan Interest",
      render: (val, row) => {
        return row?.depositplan?.interest ? row.depositplan.interest : "N.A";
      },
    },
    {
      field: "duration",
      headerName: "Plan Duration(Days)",
      render: (val, row) => {
        return row?.depositplan?.duration ? row.depositplan.duration : "N.A";
      },
    },
    {
      field: "benefit",
      headerName: "Benefit/Annum",
      render: (val, row) => {
        return row?.claimedpercentage != null
          ? `${row.claimedpercentage}`
          : "N.A";
      },
    },
    {
      field: "gift",
      headerName: "Benefits Weight (GMS)",
      render: (val, row) => {
        return row?.gift?.weight != null
          ? `${Number(row.gift.weight).toFixed(2)} GMS`
          : "N.A";
      },
    },
    {
      field: "status",
      headerName: "Status",
      render: (value, row) => {
        let label = "N.A";
        let bgColor = "#6c757d";

        switch (row.status) {
          case "RUNNING":
            label = "RUNNING";
            bgColor = "#28a745";
            break;
          case "CUSTOMERDISCONTINUED":
            label = "CUSTOMER STOPPED";
            bgColor = "#ffc107";
            break;
          case "JEWELLERDISCONTINUED":
            label = "JEWELLER STOPPED";
            bgColor = "#ff5722";
            break;
          case "COMPLETED":
            label = "COMPLETED";
            bgColor = "#2196f3";
            break;
          default:
            label = "N.A";
            bgColor = "#9e9e9e";
        }

        if (!canEdit) {
          return (
            <Box sx={{ textAlign: "center", width: "100%" }}>
              <span
                style={{
                  fontWeight: 600,
                  color: bgColor,
                }}
              >
                {label}
              </span>
            </Box>
          );
        }
        return (
          <button
            style={{
              backgroundColor: bgColor,
              color: "#fff",
              border: "none",
              padding: "5px 10px",
              borderRadius: "4px",
              cursor: "default",
            }}
          >
            {label}
          </button>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      render: (value, row) => {
        if (!canEdit) return "";
        if (row.status === "RUNNING") {
          return "N.A";
        } else if (
          row.status === "CUSTOMERDISCONTINUED" ||
          row.status === "JEWELLERDISCONTINUED" ||
          row.status === "COMPLETED"
        ) {
          return row.claimed === "" || row.claimed == null ? (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleOpenModal(row._id)}
            >
              Transfer Benefits
            </Button>
          ) : (
            <Typography variant="body2" color="primary" fontWeight={600}>
              Transferred
            </Typography>
          );
        } else {
          return "N.A";
        }
      },
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/bullion-deposit/subscribedPlanDashboardCsv",
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "subscribedPlanDashboard.csv");
        },
      }),
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(114, 28, 28, 0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Subscriber List
        </Typography>
        {/* Table Section */}
        <Box px={2} pb={2} display="flex" gap={2} flexWrap="wrap">
          {["RUNNING", "COMPLETED", "CUSTOMER STOPPED", "JEWELLER STOPPED"].map(
            (label) => (
              <Button
                key={label}
                variant={statusFilter === label ? "contained" : "outlined"}
                onClick={() => setStatusFilter(label)}
              >
                {label}
              </Button>
            ),
          )}
          <Button
            variant={!statusFilter ? "contained" : "outlined"}
            onClick={() => setStatusFilter("")}
          >
            All
          </Button>
        </Box>

        <CardContent
          sx={{
            height: "580px",
            width: "100%",
          }}
        >
          <TableCompo
            columns={columns}
            data={JSON.parse(JSON.stringify(filteredData)).reverse() || []}
            totalCount={totalSubscribers || 0}
            loading={subscriberLoading}
            page={page}
            limit={rowsPerPage}
            onTableChange={handleTableChange}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadStoreCsv}
              >
                Download CSV
              </Button>
            }
          />
        </CardContent>
      </Box>
      <Dialog open={open} onClose={handleCloseModal}>
        <DialogTitle>Transfer Benefits</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Transfer Percentage"
            type="number"
            fullWidth
            value={transferPercentage}
            onChange={(e) => setTransferPercentage(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="error">
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            variant="contained"
            color="primary"
            disabled={transferPercentage === ""}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriberList;
